import React from 'react';
import { useTranslation } from 'react-i18next';

import { UnlinkIcon } from '@/shared/assets/icons/unlink-icon';
import { ConfirmDialog } from '@/shared/components/ui/confirm-dialog/confirm-dialog';
import { Trigger } from '@/shared/components/ui/trigger/trigger';
import { Can } from '@/shared/features/permissions/components/can/can';
import { TDetachRoleProps } from '@/shared/features/rbac/components/detach-role/detach-role.types';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useRemoveRoleFromUserMutation } from '@/shared/redux/api/ident/ident-api';
import { RemoveRoleFromUserMutation } from '@/shared/types/types.generated';

export const DetachRole: React.FC<TDetachRoleProps> = ({
  userIds,
  userRoleIds,
  onClose,
  onSuccess,
  component = 'MenuItem',
  disabled,
}) => {
  const { t } = useTranslation();
  const { permissionActions } = useRbacConfig();
  const [removeRole] = useRemoveRoleFromUserMutation();

  const removeRoleFromUser = async () => {
    const promises: Promise<RemoveRoleFromUserMutation>[] = [];

    userRoleIds.forEach((userRoleId) => {
      userIds.forEach((userId) =>
        promises.push(
          removeRole({
            userId: userId!,
            userRoleId,
          }).unwrap(),
        ),
      );
    });

    await Promise.all(promises);
  };

  return (
    <Can action={permissionActions.unlinkUserRole} ids={userRoleIds}>
      <ConfirmDialog
        triggerComponent={
          <Trigger
            component={component}
            icon={<UnlinkIcon />}
            buttonVariant="secondary"
            label={t('common:detach')}
            disabled={disabled}
          />
        }
        mutation={removeRoleFromUser}
        headline={t('rbac:detach_role', {
          count: userRoleIds.length,
          fromUser: t('rbac:from_user', { count: userIds.length }),
        })}
        onClose={onClose}
        onSuccess={onSuccess}
        primaryButtonLabel={t('common:detach')}
      />
    </Can>
  );
};
